import { type FormEvent, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { CarrierClientCredentialsPayload } from '@/api/types/carriers';

type ClientCredentialsFormProps = {
  onConnect: (payload: CarrierClientCredentialsPayload) => void;
  isConnecting: boolean;
  disabled?: boolean;
};

/**
 * InPost API-keys connect form (OAuth 2.1 Client Credentials, prototype-3):
 * the Client ID + Client secret pair and the Organization ID issued by InPost.
 * Shared by the Authorization page and the setup wizard's Connect InPost step.
 */
export function ClientCredentialsForm({
  onConnect,
  isConnecting,
  disabled = false,
}: ClientCredentialsFormProps) {
  const { t } = useTranslation('authorization');
  const fieldId = useId();
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [organizationId, setOrganizationId] = useState('');

  const canSubmit =
    clientId.trim() !== '' &&
    clientSecret.trim() !== '' &&
    organizationId.trim() !== '' &&
    !isConnecting &&
    !disabled;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;
    onConnect({
      client_id: clientId.trim(),
      client_secret: clientSecret.trim(),
      organization_id: organizationId.trim(),
    });
  };

  const labelClasses = 'text-sm font-medium leading-5 text-black';

  return (
    <form onSubmit={handleSubmit} className="flex w-full max-w-[573px] flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <label htmlFor={`${fieldId}-client-id`} className={labelClasses}>
            {t('login.clientCredentials.clientId')}
          </label>
          <Input
            id={`${fieldId}-client-id`}
            value={clientId}
            onChange={(event) => setClientId(event.target.value)}
            autoComplete="off"
            spellCheck={false}
            className="h-9 rounded-[10px] bg-white"
          />
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor={`${fieldId}-client-secret`} className={labelClasses}>
            {t('login.clientCredentials.clientSecret')}
          </label>
          <Input
            id={`${fieldId}-client-secret`}
            type="password"
            value={clientSecret}
            onChange={(event) => setClientSecret(event.target.value)}
            autoComplete="new-password"
            spellCheck={false}
            className="h-9 rounded-[10px] bg-white"
          />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <label htmlFor={`${fieldId}-organization-id`} className={labelClasses}>
          {t('login.clientCredentials.organizationId')}
        </label>
        <Input
          id={`${fieldId}-organization-id`}
          value={organizationId}
          onChange={(event) => setOrganizationId(event.target.value)}
          autoComplete="off"
          spellCheck={false}
          className="h-9 rounded-[10px] bg-white"
        />
      </div>
      <div>
        <Button
          type="submit"
          variant="outline"
          className="rounded-[10px] border-black text-black shadow-none hover:bg-accent"
          disabled={!canSubmit}
        >
          {isConnecting
            ? t('login.clientCredentials.connecting')
            : t('login.clientCredentials.connect')}
        </Button>
      </div>
    </form>
  );
}
